/**
 * Tirar uma imagem do material da análise, e desfazer (E111.F1 — GUS-122, S-08 e N-14).
 *
 * No GT-2026-0066 um só botão ("não é retrato / excluir") respondia a duas perguntas e tirou da
 * análise o verso da identidade. Aqui a exclusão é ação própria, em dois passos: o botão abre o
 * motivo, e só "Confirmar exclusão" grava — com o motivo, quem e quando, que o relatório imprime.
 *
 * Excluída, a imagem mostra o registro e o "Desfazer exclusão"; depois de alguma peça concluída,
 * os botões ficam travados (o material congela).
 */
import { useState } from 'react';

import { Button, Field, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  desfazerExclusaoDaAnalise,
  DocumentoDeApoioError,
  excluirDaAnalise,
  type DocumentoDeApoioData,
  type ExclusaoDaAnaliseData,
} from '@/lib/documentos_de_apoio';

const t = ptBR.documentosDeApoio.exclusao;
const MINIMO_DO_MOTIVO = 3;

function mensagemDaFalha(err: unknown): string {
  if (!(err instanceof DocumentoDeApoioError)) return t.erro;
  if (err.falha === 'foto_do_caso') return t.erroFotoDoCaso;
  if (err.falha === 'nao_e_imagem') return t.erroNaoEImagem;
  if (err.falha === 'concluido') return ptBR.documentosDeApoio.concluido;
  return t.erro;
}

function dataDoRegistro(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export interface ExclusaoDaAnaliseProps {
  caseId: string;
  documentoId: string;
  exclusao: ExclusaoDaAnaliseData | null | undefined;
  bloqueado: boolean;
  /** A exclusão foi gravada ou desfeita: quem escuta recarrega a lista e o exame. */
  onAlterado: (documento: DocumentoDeApoioData) => void;
}

export function ExclusaoDaAnalise({
  caseId,
  documentoId,
  exclusao,
  bloqueado,
  onAlterado,
}: ExclusaoDaAnaliseProps): JSX.Element {
  const client = useApiClient();
  const [aberta, setAberta] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [emVoo, setEmVoo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const idPrefix = `exclusao-${documentoId}`;

  const executar = (acao: () => Promise<DocumentoDeApoioData>): void => {
    void (async () => {
      setEmVoo(true);
      setErro(null);
      try {
        const gravado = await acao();
        setAberta(false);
        setMotivo('');
        onAlterado(gravado);
      } catch (err) {
        setErro(mensagemDaFalha(err));
      } finally {
        setEmVoo(false);
      }
    })();
  };

  const confirmar = (): void => {
    const limpo = motivo.trim();
    if (limpo.length < MINIMO_DO_MOTIVO) {
      setErro(t.erroMotivo);
      return;
    }
    executar(() => excluirDaAnalise(client, caseId, documentoId, limpo));
  };

  if (exclusao) {
    return (
      <div className="space-y-sc-2" data-testid={idPrefix}>
        <p className="text-body-sm text-fg-muted" data-testid={`${idPrefix}-registro`}>
          {t.registrada
            .replace('{data}', dataDoRegistro(exclusao.excluida_em))
            .replace('{motivo}', exclusao.motivo)}
        </p>
        <Button
          type="button"
          variant="secondary"
          disabled={bloqueado || emVoo}
          onClick={() => executar(() => desfazerExclusaoDaAnalise(client, caseId, documentoId))}
          data-testid={`${idPrefix}-desfazer`}
        >
          {emVoo ? t.desfazendo : t.desfazer}
        </Button>
        {erro !== null ? (
          <p role="alert" className="text-fg-danger text-body-sm" data-testid={`${idPrefix}-erro`}>
            {erro}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-sc-2" data-testid={idPrefix}>
      {aberta ? (
        <>
          <p className="max-w-prose text-body-sm text-fg-muted">{t.ajuda}</p>
          <Field
            label={t.motivo}
            description={t.motivoDica}
            required
            htmlFor={`${idPrefix}-motivo`}
          >
            <Textarea
              id={`${idPrefix}-motivo`}
              value={motivo}
              maxLength={500}
              disabled={emVoo}
              onChange={(e) => setMotivo(e.target.value)}
              data-testid={`${idPrefix}-motivo`}
            />
          </Field>
          <div className="flex flex-wrap gap-sc-2">
            <Button
              type="button"
              variant="danger"
              disabled={bloqueado || emVoo}
              onClick={confirmar}
              data-testid={`${idPrefix}-confirmar`}
            >
              {emVoo ? t.confirmando : t.confirmar}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={emVoo}
              onClick={() => {
                setAberta(false);
                setErro(null);
              }}
              data-testid={`${idPrefix}-cancelar`}
            >
              {t.cancelar}
            </Button>
          </div>
        </>
      ) : (
        <Button
          type="button"
          variant="ghost"
          disabled={bloqueado}
          onClick={() => setAberta(true)}
          data-testid={`${idPrefix}-abrir`}
        >
          {t.excluir}
        </Button>
      )}
      {erro !== null ? (
        <p role="alert" className="text-fg-danger text-body-sm" data-testid={`${idPrefix}-erro`}>
          {erro}
        </p>
      ) : null}
    </div>
  );
}
